/**
 * One shared record of the token the native shell handed us.
 *
 * useHybridAuth used to keep this in component state, so every screen read
 * localStorage on its own mount. Home could be polling happily on a token it
 * read an hour ago while a call screen opened a second later found the key
 * gone and treated the driver as logged out, with no request ever sent
 * (Lima Tow, Oct 1 2026). Keeping the token in one module-level store means
 * every screen agrees on whether there is a session, and a token the app
 * already holds in memory is never forgotten because a storage key vanished.
 *
 * The store only changes through activate() and clear(). The 401 handlers
 * that remove the storage key do not touch it: a dead token then fails its
 * request and that request's 401 is what tells the UI the session expired.
 */
import { isEmbeddedMode } from './embeddedMode';
import { devLog } from './devLog';

type Listener = () => void;

let token: string | null = null;
let bootstrapped = false;
let teardown: (() => void) | null = null;
const listeners = new Set<Listener>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

export const nativeSession = {
  getToken: (): string | null => token,

  activate: (next: string): void => {
    if (next === token) return;
    token = next;
    emit();
  },

  clear: (): void => {
    if (token === null) return;
    token = null;
    emit();
  },

  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Pick up the token the shell wrote before React mounted, and keep
   * listening for the synthetic storage events it fires on re-injection.
   * Runs once per page load no matter how many hooks call it.
   */
  bootstrap: (onToken: (next: string) => void): void => {
    if (bootstrapped || !isEmbeddedMode()) return;
    bootstrapped = true;

    const adopt = (next: string | null) => {
      if (next && next !== token) {
        onToken(next);
      }
    };

    adopt(localStorage.getItem('access_token'));

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'access_token' && event.newValue) {
        devLog('[nativeSession] Token updated via storage event');
        adopt(event.newValue);
      }
    };
    window.addEventListener('storage', onStorage);

    // The shell may inject a beat after first paint, and the storage event
    // does not always fire. Poll for the first 5 seconds, then stop.
    const interval = window.setInterval(() => {
      adopt(localStorage.getItem('access_token'));
    }, 500);
    const stopPolling = window.setTimeout(
      () => window.clearInterval(interval),
      5000
    );

    teardown = () => {
      window.removeEventListener('storage', onStorage);
      window.clearInterval(interval);
      window.clearTimeout(stopPolling);
    };
  },

  /** Test only. */
  _reset: (): void => {
    teardown?.();
    teardown = null;
    token = null;
    bootstrapped = false;
    listeners.clear();
  },
};
